


















import Component, { mixins } from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { Route } from "vue-router";
import { Follower, FollowingsSearchResult } from "@scrile/api-provider/dist/api/FollowingsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import WithAuthUser from "@/mixins/WithAuthUser";
import providers from "@/lib/api";
import { AuthorisedOnly, CheckRole } from "@/lib/decorators";
import { resolveUserIdByRoute } from "@/lib/userHelpers";
import config from "@/config";
import FollowersView from "./components/FollowersView";
import { FollowersMap } from "./types";

@Component({ name: "PageFollowers", components: { FollowersView } })
export default class PageFollowers extends mixins(WithAuthUser) {
  @Prop({ type: String, required: true })
  public readonly userId!: string;

  @Prop({ type: Boolean, required: true })
  public readonly isFollowingPage!: boolean;

  public loading: boolean = false;

  public scroll: boolean = false;

  public user: UserPublicData | null = null;

  public followingsSearchResult: FollowingsSearchResult | null = null;

  public authorFollowings: FollowersMap = {};

  public get authUserId() {
    return this.authUser?.id ?? "";
  }

  public get isAuthor() {
    return this.authUserId === this.userId;
  }

  public get canGetFollowings() {
    return config.featurePublicFollowing || this.isAuthor || this.isAdmin;
  }

  public get canGetFollowers() {
    return config.featurePublicFollowers || this.isAuthor || this.isAdmin;
  }

  @CheckRole("admin")
  public isAdmin!: boolean;

  @Watch("isFollowingPage")
  public async watchFollowingType(isFollowingPage: boolean) {
    try {
      this.followingsSearchResult = null;
      this.loading = true;
      this.authorFollowings = {};
      await this.getFollowers(isFollowingPage);
    } finally {
      this.loading = false;
    }
  }

  public async getFollowers(isFollowingPage: boolean, page: number = 0, replace: boolean = true) {
    if (!this.canGetFollowers && !this.canGetFollowings) return;
    this.scroll = false;
    try {
      const inputData = isFollowingPage ? { followerId: this.userId } : { userId: this.userId };
      const response = await providers.FollowingsProvider.find({
        data: { ...inputData, sort: "TIME,DESC", page, size: 20 },
      });
      this.followingsSearchResult = {
        ...response,
        result:
          replace || !this.followingsSearchResult
            ? response.result
            : this.followingsSearchResult.result.concat(response.result),
      };

      if (this.authUser && response.result.length !== 0) {
        const reduceToMapCallback = (acc: FollowersMap, i: Follower) => {
          acc[i.userId] = i;
          return acc;
        };
        if (this.isAuthor && isFollowingPage) {
          this.authorFollowings = this.followingsSearchResult.result.reduce<FollowersMap>(reduceToMapCallback, {});
        } else {
          const buffer = await providers.FollowingsProvider.searchFollowings({
            ids: response.result.map(i => (isFollowingPage ? i.userId : i.followerId)),
          });
          const followersMap = buffer.reduce<FollowersMap>(reduceToMapCallback, {});
          this.authorFollowings = { ...this.authorFollowings, ...followersMap };
        }
      }
    } finally {
      this.scroll = this.followingsSearchResult?.hasNextPage ?? false;
    }
  }

  public getNextPage(cb: Function) {
    const nextPage = (this.followingsSearchResult?.page ?? 0) + 1;
    this.getFollowers(this.isFollowingPage, nextPage, false).finally(() => cb());
  }

  public async created() {
    const featureFlag = this.isFollowingPage ? config.featurePublicFollowing : config.featurePublicFollowers;
    if (!featureFlag && !this.isAuthor && !this.isAdmin) {
      this.$router.push("/");
      return;
    }
    try {
      this.loading = true;
      const { result } = await providers.UserPublicDataProvider.search({ data: { id: [this.userId] } });
      this.user = result?.[0] ?? null;

      await this.getFollowers(this.isFollowingPage);
    } finally {
      this.loading = false;
    }
  }

  @AuthorisedOnly
  public async toggleFollow(userToToggle: UserPublicData, cb: Function) {
    const result = await providers.FollowingsProvider.toggle({ id: userToToggle.id });
    cb(result);
  }

  public beforeRouteEnter(to: Route, from: Route, next: any) {
    resolveUserIdByRoute(to, from, next);
  }
}
